// Date utility functions for EST timezone (New York time)

const EST_TIMEZONE = 'America/New_York';

/**
 * Get time ago string in EST timezone
 * Examples: "Just now", "2 min ago", "5 hours ago", "2 days ago"
 */
export const getTimeAgoEST = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(seconds / 3600);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }

  const days = Math.floor(seconds / 86400);
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  // For older dates, show formatted date in EST
  return formatDateEST(dateString);
};

/**
 * Format date in EST timezone
 * Example: "Jan 15, 2024" or "Jan 15" (same year)
 */
export const formatDateEST = (dateString, includeYear = 'auto') => {
  const date = new Date(dateString);
  const now = new Date();

  const options = {
    month: 'short',
    day: 'numeric',
    timeZone: EST_TIMEZONE
  };

  if (includeYear === true || (includeYear === 'auto' && date.getFullYear() !== now.getFullYear())) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString('en-US', options);
};

/**
 * Format date with time in EST timezone
 * Example: "Jan 15, 2024 10:30 AM EST"
 */
export const formatDateTimeEST = (dateString) => {
  const date = new Date(dateString);

  const dateOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: EST_TIMEZONE
  };

  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: EST_TIMEZONE
  };

  const datePart = date.toLocaleDateString('en-US', dateOptions);
  const timePart = date.toLocaleTimeString('en-US', timeOptions);

  return `${datePart} ${timePart} EST`;
};

/**
 * Format time only in EST timezone
 * Example: "10:30 AM EST"
 */
export const formatTimeEST = (dateString) => {
  const date = new Date(dateString);

  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: EST_TIMEZONE
  };

  return date.toLocaleTimeString('en-US', options) + ' EST';
};

/**
 * Format date for input fields (YYYY-MM-DD) in EST timezone
 */
export const formatDateForInput = (dateString) => {
  const date = new Date(dateString);

  // Convert to EST and format as YYYY-MM-DD
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: EST_TIMEZONE }));

  const year = estDate.getFullYear();
  const month = String(estDate.getMonth() + 1).padStart(2, '0');
  const day = String(estDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Get current date in EST timezone (YYYY-MM-DD format)
 */
export const getCurrentDateEST = () => {
  const now = new Date();
  const estDate = new Date(now.toLocaleString('en-US', { timeZone: EST_TIMEZONE }));

  const year = estDate.getFullYear();
  const month = String(estDate.getMonth() + 1).padStart(2, '0');
  const day = String(estDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Check if date is today in EST timezone
 */
export const isTodayEST = (dateString) => {
  const date = new Date(dateString);
  const today = getCurrentDateEST();
  const inputDate = formatDateForInput(date);

  return inputDate === today;
};

/**
 * Check if date is within current week in EST timezone
 */
export const isThisWeekEST = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();

  // Get EST dates
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: EST_TIMEZONE }));
  const estNow = new Date(now.toLocaleString('en-US', { timeZone: EST_TIMEZONE }));

  // Get start of week (Sunday)
  const startOfWeek = new Date(estNow);
  startOfWeek.setDate(estNow.getDate() - estNow.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  // Get end of week (Saturday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return estDate >= startOfWeek && estDate <= endOfWeek;
};
